/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-unused-prop-types */

import { Fragment, useCallback, useMemo } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordPill } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { ProductClicked } from '@zola/tracking-contracts/src/events';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import { TagV2Size } from '@zola/zola-ui/src/components/Tag/TagV2';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';

import cx from 'classnames';

import {
  getDisplayPrice,
  getPerPriceText,
  getVendorsPerPrice,
  showPerEntityPricing,
} from '~/components/common/cards/util/helper';
import { doTrackVendorCardClickAndView } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { formatLocation } from '~/util/formatters';
import { getImageUrl, ImageFit } from '~/util/imageUtils';
import { formatRating } from '~/util/reviewUtils';

import { ResponsiveVendorTagV2Content } from '../../badges/ResponsiveVendorTagV2';
import VendorIcon from '../../icons/VendorIcon';
import CardBase, { CardBaseProps } from '../CardBase/CardBase';
import { VendorCardVendor } from '../util/types';
import {
  BeauticianCardDetails,
  MusicianDetails,
  PhotographerDetails,
  PlannerServiceLevels,
  ServiceLocationDetails,
  VenueCapacityCardDetails,
  VenueServiceLevelCardDetails,
  VenueSettingCardDetails,
  VideographerDetails,
} from '../VendorCard/components/CardDetails';
import VendorTypeAndLocation from '../VendorCard/components/VendorTypeAndLocation';

import styles from './smallVendorCard.module.less';

type V2CardDetailProps = {
  showPricing?: boolean;
  showCategory?: boolean;
  showLocation?: boolean;
  showDetails?: boolean;
  showRating?: boolean;
} & (
  | {
      showServiceLocation: true;
      queriedLocation: string;
    }
  | {
      /**
       * When search in an area, and we want to display serves this location, this indicates
       * what location was searched and how to interpret the metro type / home market of the
       * vendor
       */
      showServiceLocation?: false;
      queriedLocation?: string;
    }
) &
  (
    | {
        trackClick: true;
        position: number;
        listingShelfName?: ProductClicked['listing_shelf_name'];
      }
    | {
        trackClick: false;
      }
  );

type V2CardProps = {
  v2: true;

  internalLink: string;

  /** What size is the image on the card: large: 100px, default: 64px. V2 cards only */
  size?: 'large' | 'default';

  /** Align the info in the center of the card.  Default: false, align them at the top */
  alignCenter?: boolean;
} & V2CardDetailProps;

type V1CardProps = {
  v2?: false;
  internalLink?: string;
};

type BaseProps = {
  className?: string | Record<string, boolean>;
  storefront: {
    type: VendorTaxonomyKey;
    name: string | null;
    vendorCard: VendorCardVendor | null;
  };
} & Pick<CardBaseProps, 'onClick' | 'style'>;

const VenueDetails = ({ vendorCard }: { vendorCard: VendorCardVendor | null }): JSX.Element[] => {
  const { details } = vendorCard || {};

  const parts: JSX.Element[] = [
    VenueCapacityCardDetails,
    VenueServiceLevelCardDetails,
    VenueSettingCardDetails,
  ]
    .map((component) => component({ details: details ?? null, className: styles.infoRow }))
    .filter(Boolean) as JSX.Element[];

  return parts;
};

const Pricing = ({ vendorCard }: { vendorCard: VendorCardVendor | null }): JSX.Element | null => {
  const { taxonomyNodeKey, price, details, priceRangeVisible } = vendorCard || {};

  const hasNoMinimumPrice = price?.min === null || price?.min === undefined;
  const whichDisplayPrice = showPerEntityPricing(taxonomyNodeKey, price)
    ? getVendorsPerPrice(taxonomyNodeKey, details)
    : getDisplayPrice(price?.min);
  const displayPrice = priceRangeVisible ? whichDisplayPrice : null;
  const perPriceCopy = hasNoMinimumPrice && taxonomyNodeKey ? getPerPriceText(taxonomyNodeKey) : '';

  if (displayPrice) {
    return (
      <div className={styles.infoRow} data-testid="pricing">
        Starts at {displayPrice} {perPriceCopy}
      </div>
    );
  }
  return null;
};

type VendorCardVendorWithRating = VendorCardVendor & {
  reviewsCount: number;
  averageReviewsRate: number;
};
export const hasRating = (
  vendorCard: VendorCardVendor | null
): vendorCard is VendorCardVendorWithRating => {
  const { reviewsCount, averageReviewsRate } = vendorCard || {};

  return Boolean(reviewsCount) && Boolean(averageReviewsRate);
};

const Rating = ({ vendorCard }: { vendorCard: VendorCardVendor | null }): JSX.Element | null => {
  if (hasRating(vendorCard)) {
    const { reviewsCount, averageReviewsRate } = vendorCard;
    return (
      <div className={styles.infoRow}>
        <StarRatings
          activeStars={formatRating(averageReviewsRate)}
          fillColor={COLORS3.YELLOW_100}
          reviewCount={reviewsCount.toString()}
          textSize="small"
          totalStars={5}
        />
      </div>
    );
  }
  return null;
};

const BasicLocation = ({ vendorCard }: { vendorCard: VendorCardVendor }): JSX.Element | null => {
  const { city, stateProvince } = vendorCard || {};
  if (city && stateProvince) {
    return (
      <div className={styles.infoRow}>
        {city}, {stateProvince}
      </div>
    );
  }
  return null;
};
export type SmallVendorCardProps = BaseProps & (V1CardProps | V2CardProps);

export type V2CardField =
  | 'basic-location'
  | 'location'
  | 'location-category'
  | 'category'
  | 'price'
  | 'rating'
  | 'service-location'
  | 'details'
  | 'tags';
export type V2CardFieldConfig = {
  field: V2CardField;
  priority: number;
};
type V2CardFieldWithRenderedComponent = V2CardFieldConfig & {
  element: React.ReactNode;
};

type SelectedV2Field = V2CardFieldConfig & {
  element: React.ReactNode;
  keep: boolean;
};

export const V2ConfigurableCardDetails = ({
  vendorCard,
  maxDetailRows,
  queriedLocation,
  configuration,
}: {
  vendorCard: VendorCardVendor;
  maxDetailRows: number;
  queriedLocation?: string | undefined;
  configuration: V2CardFieldConfig[];
}) => {
  // Go through the config, and add in what would render if that element was put on the page
  const configWithComponents: V2CardFieldWithRenderedComponent[] = useMemo(() => {
    const withComponents: V2CardFieldWithRenderedComponent[] = [];

    configuration.forEach((fieldConfig) => {
      const { field } = fieldConfig;
      if (field === 'location' || field === 'location-category' || field === 'category') {
        const { city, stateProvince } = vendorCard || {};

        const showLocation = field !== 'category';
        const showCategory = field !== 'location';

        // This is a muted text color
        withComponents.push({
          ...fieldConfig,
          element: VendorTypeAndLocation({
            className: styles.infoRow,
            vendorTypeKey: vendorCard.taxonomyNodeKey,
            displayLocation:
              showLocation && city && stateProvince ? `${city}, ${stateProvince}` : null,
            showVendorType: showCategory,
          }),
        });
      }
      if (field === 'basic-location') {
        withComponents.push({
          ...fieldConfig,
          element: BasicLocation({ vendorCard }),
        });
      }
      if (field === 'price') {
        withComponents.push({
          ...fieldConfig,
          element: Pricing({ vendorCard }),
        });
      }
      if (field === 'rating') {
        withComponents.push({
          ...fieldConfig,
          element: Rating({ vendorCard }),
        });
      }
      if (field === 'details') {
        // Note the multiple items here
        const details = [
          ...VenueDetails({ vendorCard }),
          PlannerServiceLevels({
            details: vendorCard?.details,
            className: styles.infoRow,
          }),
          MusicianDetails({
            details: vendorCard?.details,
            className: styles.infoRow,
          }),
          PhotographerDetails({
            details: vendorCard?.details,
            className: styles.infoRow,
          }),
          VideographerDetails({
            details: vendorCard?.details,
            className: styles.infoRow,
          }),
          BeauticianCardDetails({
            details: vendorCard?.details,
            className: styles.infoRow,
          }),
        ].filter(Boolean) as JSX.Element[];

        const flatDetails = details.map((element) => {
          return {
            ...fieldConfig,
            element,
          };
        });

        withComponents.push(...flatDetails);
      }
      if (field === 'service-location') {
        withComponents.push({
          ...fieldConfig,
          element: ServiceLocationDetails({
            metroType: vendorCard?.metroType,
            queriedLocation,
            className: styles.infoRow,
            awayServiceText: (l) => `Serves ${l} for a fee`,
            homeText: (l) => `Based in ${l}`,
          }),
        });
      }
      if (field === 'tags') {
        const renderedTag = (
          <ResponsiveVendorTagV2Content
            className={styles.tag}
            size={TagV2Size.SMALL}
            badges={vendorCard.badges}
          />
        );

        if (renderedTag) {
          withComponents.push({
            ...fieldConfig,
            element: (
              <div className={styles.infoRow} style={{ position: 'relative' }}>
                {renderedTag}
              </div>
            ),
          });
        }
      }
    });

    return withComponents;
  }, [configuration, queriedLocation, vendorCard]);

  const minPriority = useMemo(() => {
    return configuration.reduce((min, { priority }) => {
      if (priority < min) {
        return priority;
      }
      return min;
    }, 0);
  }, [configuration]);

  const maxPriority = useMemo(() => {
    return configuration.reduce((max, { priority }) => {
      if (priority > max) {
        return priority;
      }
      return max;
    }, 0);
  }, [configuration]);

  // Go through the parts and pick
  // - the most important thing to show (highest priority + lower position in the list)
  // - keep doing that until we hit the limit for how many things we are going to show
  const partsToRender = useMemo(() => {
    const selectedParts: SelectedV2Field[] = configWithComponents.map((fieldConfig) => {
      return {
        ...fieldConfig,
        keep: false,
      };
    });

    let numKept = 0;

    // Start with the highest priority (lower numbers) and go to the lowest priority (higher numbers)
    for (let currentPriority = minPriority; currentPriority <= maxPriority; currentPriority++) {
      // Find all the candidate elements with this priority
      const candidates = selectedParts.filter(
        ({ priority, element }) => priority === currentPriority && Boolean(element)
      );

      // Mark elements to show (keep = true) until we hit the limit
      candidates.forEach((candidate) => {
        if (numKept < maxDetailRows && candidate.keep === false) {
          candidate.keep = true;
          numKept = numKept + 1;
        }
      });
    }

    // Throw out any parts that won't be shown
    return selectedParts.filter(({ keep }) => keep);
  }, [configWithComponents, maxDetailRows, maxPriority, minPriority]);

  return (
    <Fragment>
      {partsToRender.map(({ element }, index) => (
        <Fragment key={index}>{element}</Fragment>
      ))}
    </Fragment>
  );
};

export const V2CardDetails = ({
  showLocation = true,
  showCategory = true,
  showPricing = true,
  showDetails = false,
  showRating = false,
  showServiceLocation = false,
  ...rest
}: V2CardDetailProps & { vendorCard: VendorCardVendor; maxDetailRows?: number }) => {
  const config = useMemo(() => {
    const tempConfig: V2CardFieldConfig[] = [];

    if (showLocation && showCategory) {
      tempConfig.push({
        field: 'location-category',
        priority: 1,
      });
    } else if (showLocation) {
      tempConfig.push({
        field: 'location',
        priority: 1,
      });
    } else if (showCategory) {
      tempConfig.push({
        field: 'category',
        priority: 1,
      });
    }

    if (showServiceLocation) {
      tempConfig.push({
        field: 'service-location',
        priority: 1,
      });
    }
    if (showDetails) {
      tempConfig.push({
        field: 'details',
        priority: 2,
      });
    }

    if (showPricing) {
      tempConfig.push({
        field: 'price',
        priority: 1,
      });
    }

    if (showRating) {
      tempConfig.push({
        field: 'rating',
        priority: 1,
      });
    }
    return tempConfig;
  }, [showCategory, showDetails, showLocation, showPricing, showRating, showServiceLocation]);

  return <V2ConfigurableCardDetails maxDetailRows={3} {...rest} configuration={config} />;
};

const V2Card = ({ storefront, ...rest }: BaseProps & V2CardProps) => {
  return (
    <div className={styles.info}>
      <div className={styles.name}>{storefront.name}</div>

      {storefront.vendorCard && (
        <V2CardDetails {...rest} vendorCard={storefront.vendorCard} {...rest} />
      )}
    </div>
  );
};

const V1Card = ({ storefront }: BaseProps & V1CardProps) => {
  const { city, stateProvince } = storefront.vendorCard || {};

  const location = city && stateProvince ? formatLocation({ city, stateProvince }) : '';

  return (
    <div className={styles.info}>
      <div className={styles.category}>{getVendorWordPill(storefront.type)}</div>
      <div className={styles.name}>{storefront.name}</div>
      {location && <div className={styles.location}>{location}</div>}
    </div>
  );
};
const SmallVendorCard = (props: SmallVendorCardProps): JSX.Element => {
  const { className, storefront, internalLink, onClick, style } = props;

  const { storefrontCoverId } = storefront.vendorCard || {};

  const imageUrl = storefrontCoverId
    ? getImageUrl(storefrontCoverId, { width: 200, height: 200, fit: ImageFit.CROP })
    : '';

  const doClick = useCallback(
    (event: React.MouseEvent) => {
      if (onClick) {
        onClick(event);
      }
      if (props.v2 && props.trackClick) {
        const { position, listingShelfName } = props;
        const { vendorCard } = storefront;

        if (vendorCard) {
          doTrackVendorCardClickAndView({
            vendor: vendorCard,
            position,
            listingShelfName,
          });
        }
      }
    },
    [onClick, props, storefront]
  );

  return (
    <CardBase
      className={cx(styles.smallVendorCard, className, {
        [styles.v2]: props.v2,
        [styles.large]: props.v2 && props.size === 'large',
        [styles.alignCenter]: props.v2 && props.alignCenter,
      })}
      internalLink={internalLink}
      onClick={doClick}
      style={style}
    >
      <div className={styles.imageContainer}>
        {imageUrl ? (
          <img className={styles.image} src={imageUrl} alt="" />
        ) : (
          <VendorIcon vendorTaxonomyKey={storefront.type} />
        )}
      </div>
      {props.v2 ? <V2Card {...props} /> : <V1Card {...props} />}
    </CardBase>
  );
};

export default SmallVendorCard;
